<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import type { Building } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"
import {
    EnergyUse,
    type EnergyUseForm,
} from "@modules/energy-manager/helpers/energyuse"

// ================================
// PROPS
// ================================
const props = defineProps<{
    readonly: boolean
    /** Bâtiment en consultation seule. */
    disabled?: boolean
    /** Cible `infos.batiment` ou `infos.logement` pour `energyuse`. */
    scope: "batiment" | "logement"
}>()

const getFieldReadonly = computed(() => props.readonly && !props.disabled)
const getFieldDisabled = computed(() => Boolean(props.disabled))
const getIsControlLocked = computed(
    () => props.readonly || Boolean(props.disabled)
)

// ================================
// STORES
// ================================
const buildingStore = useBuildingStore()
const { building } = storeToRefs(buildingStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            sectionEquipmentPresent: "Equipements présents",
            sectionHeatingTypology:
                "Typologie du chauffage et d’eau chaude sanitaire",
            electricVehicle: "Véhicule électrique",
            pool: "Piscine",
            sauna: "Sauna",
            heating: "Chauffage",
            hotWater: "Eau chaude sanitaire",
            radiatorsCount: "Nombre de radiateurs",
        },
    },
})

const chauffageOptions = [
    { label: "Radiateurs électriques", value: "elec_radiateur" },
    { label: "Pompe à chaleur", value: "elec_pac" },
    { label: "Chaudière gaz", value: "gaz" },
    { label: "Chaudière fioul", value: "fioul" },
]

const ecsOptions = [
    { label: "Ballon d'eau chaude électrique", value: "elec_ballon" },
    { label: "Chaudière gaz", value: "gaz" },
    { label: "Chaudière fioul", value: "fioul" },
    { label: "Chauffe-eau thermodynamique", value: "thermodynamique" },
]

const idPrefix = computed(
    () => `building-info-general-equipement-${props.scope}`
)

// ================================
// ÉTAT — aligné sur BuildingInfoGeneralBuilding (energyuse)
// ================================
const vehiculeElectrique = ref(false)
const piscine = ref(false)
const sauna = ref(false)
const chauffage = ref<EnergyUseForm["chauffage"]>(null)
const ecs = ref<EnergyUseForm["ecs"] | "thermodynamique">(null)
const radiateursElectriques = ref(0)
const radiateursEau = ref(0)

function getInfosSlice():
    | Building["infos"]["batiment"]
    | Building["infos"]["logement"]
    | undefined {
    const b = building.value
    if (!b?.infos) return undefined
    return props.scope === "batiment" ? b.infos.batiment : b.infos.logement
}

function syncEnergyUseFromBuilding(): void {
    const slice = getInfosSlice()
    const energyUse = new EnergyUse(
        (slice?.energyuse ?? []) as ConstructorParameters<typeof EnergyUse>[0]
    )
    const energyUseForm = energyUse.createFormFromGraph()

    vehiculeElectrique.value =
        energyUseForm.equipements_elec.includes("voiture")
    piscine.value = energyUseForm.equipements_elec.includes("piscine")
    sauna.value = energyUseForm.equipements_elec.includes("sauna")
    chauffage.value = energyUseForm.chauffage
    ecs.value =
        energyUseForm.ecs === "elec_ballon" ? "elec_ballon" : energyUseForm.ecs
    radiateursElectriques.value = energyUseForm.radiateurs.elec ?? 0
    radiateursEau.value = energyUseForm.radiateurs.eau ?? 0
}

watch(building, syncEnergyUseFromBuilding, { immediate: true, deep: true })

function getEnergyUsePayload():
    | Building["infos"]["batiment"]["energyuse"]
    | Building["infos"]["logement"]["energyuse"] {
    const energyUse = new EnergyUse()

    energyUse.createGraphFromForm({
        chauffage: chauffage.value,
        ecs: ecs.value === "thermodynamique" ? "elec_ballon" : ecs.value,
        equipements_elec: [
            vehiculeElectrique.value ? "voiture" : null,
            piscine.value ? "piscine" : null,
            sauna.value ? "sauna" : null,
        ].filter(Boolean) as string[],
        radiateurs: {
            elec: radiateursElectriques.value,
            eau: radiateursEau.value,
        },
    })

    return energyUse.getNodes() as Building["infos"]["batiment"]["energyuse"]
}

function getBaselineEnergyStateFromBuilding(b: Building): {
    vehiculeElectrique: boolean
    piscine: boolean
    sauna: boolean
    chauffage: EnergyUseForm["chauffage"]
    ecs: EnergyUseForm["ecs"] | "thermodynamique" | null
    radiateursElectriques: number
    radiateursEau: number
} {
    const slice =
        props.scope === "batiment" ? b.infos?.batiment : b.infos?.logement
    const energyUse = new EnergyUse(
        (slice?.energyuse ?? []) as ConstructorParameters<typeof EnergyUse>[0]
    )
    const energyUseForm = energyUse.createFormFromGraph()
    return {
        vehiculeElectrique: energyUseForm.equipements_elec.includes("voiture"),
        piscine: energyUseForm.equipements_elec.includes("piscine"),
        sauna: energyUseForm.equipements_elec.includes("sauna"),
        chauffage: energyUseForm.chauffage,
        ecs:
            energyUseForm.ecs === "elec_ballon"
                ? "elec_ballon"
                : energyUseForm.ecs,
        radiateursElectriques: energyUseForm.radiateurs.elec ?? 0,
        radiateursEau: energyUseForm.radiateurs.eau ?? 0,
    }
}

function hasEnergyUseChanged(): boolean {
    const b = building.value
    if (!b) return false
    const base = getBaselineEnergyStateFromBuilding(b)
    return (
        vehiculeElectrique.value !== base.vehiculeElectrique ||
        piscine.value !== base.piscine ||
        sauna.value !== base.sauna ||
        chauffage.value !== base.chauffage ||
        ecs.value !== base.ecs ||
        radiateursElectriques.value !== base.radiateursElectriques ||
        radiateursEau.value !== base.radiateursEau
    )
}

const getShowRadiateursElectriques = computed(
    () => chauffage.value === "elec_radiateur"
)
const getShowRadiateursEau = computed(
    () => !!chauffage.value && chauffage.value !== "elec_radiateur"
)

defineExpose({
    syncFromStore: syncEnergyUseFromBuilding,
    getEnergyUsePayload,
    hasEnergyUseChanged,
})
</script>

<template>
    <!-- Equipements présents -->
    <fieldset class="flex flex-col gap-4 border-0 p-0 m-0">
        <legend
            class="md:col-span-2 text-base font-semibold text-surface-900 mb-4"
        >
            {{ t("sectionEquipmentPresent") }}
        </legend>
        <div class="flex flex-wrap gap-6">
            <div class="flex items-center gap-2">
                <BaseCheckbox
                    v-model="vehiculeElectrique"
                    binary
                    :input-id="`${idPrefix}-vehicule`"
                    :disabled="getIsControlLocked"
                />
                <label
                    :for="`${idPrefix}-vehicule`"
                    class="text-sm text-surface-700 select-none"
                >
                    {{ t("electricVehicle") }}
                </label>
            </div>
            <div class="flex items-center gap-2">
                <BaseCheckbox
                    v-model="piscine"
                    binary
                    :input-id="`${idPrefix}-piscine`"
                    :disabled="getIsControlLocked"
                />
                <label
                    :for="`${idPrefix}-piscine`"
                    class="text-sm text-surface-700 select-none"
                >
                    {{ t("pool") }}
                </label>
            </div>
            <div class="flex items-center gap-2">
                <BaseCheckbox
                    v-model="sauna"
                    binary
                    :input-id="`${idPrefix}-sauna`"
                    :disabled="getIsControlLocked"
                />
                <label
                    :for="`${idPrefix}-sauna`"
                    class="text-sm text-surface-700 select-none"
                >
                    {{ t("sauna") }}
                </label>
            </div>
        </div>
    </fieldset>

    <!-- Typologie chauffage / ECS -->
    <fieldset class="grid grid-cols-1 md:grid-cols-2 gap-4 border-0 p-0 m-0">
        <legend
            class="md:col-span-2 text-base font-semibold text-surface-900 mb-4"
        >
            {{ t("sectionHeatingTypology") }}
        </legend>
        <BaseInputWrapper
            :label="t('heating')"
            :input-id="`${idPrefix}-heating`"
            unfloat
        >
            <BaseSelect
                v-model="chauffage"
                :input-id="`${idPrefix}-heating`"
                :options="chauffageOptions"
                option-label="label"
                option-value="value"
                class="w-full"
                :disabled="getIsControlLocked"
                show-clear
                placeholder="—"
            />
        </BaseInputWrapper>
        <BaseInputWrapper
            :label="t('hotWater')"
            :input-id="`${idPrefix}-hot-water`"
            unfloat
        >
            <BaseSelect
                v-model="ecs"
                :input-id="`${idPrefix}-hot-water`"
                :options="ecsOptions"
                option-label="label"
                option-value="value"
                class="w-full"
                :disabled="getIsControlLocked"
                show-clear
                placeholder="—"
            />
        </BaseInputWrapper>
        <BaseInputWrapper
            v-if="getShowRadiateursElectriques"
            :label="t('radiatorsCount')"
            :input-id="`${idPrefix}-electric-radiators`"
            unfloat
        >
            <BaseInputNumber
                v-model="radiateursElectriques"
                :input-id="`${idPrefix}-electric-radiators`"
                class="w-full"
                :min="0"
                show-buttons
                button-layout="horizontal"
                decrement-button-icon="pi pi-minus"
                increment-button-icon="pi pi-plus"
                :readonly="getFieldReadonly"
                :disabled="getFieldDisabled"
                :style="{
                    '--p-inputgroup-addon-border-radius': '0',
                }"
            />
        </BaseInputWrapper>
        <BaseInputWrapper
            v-else-if="getShowRadiateursEau"
            :label="t('radiatorsCount')"
            :input-id="`${idPrefix}-water-radiators`"
            unfloat
        >
            <BaseInputNumber
                v-model="radiateursEau"
                :input-id="`${idPrefix}-water-radiators`"
                class="w-full"
                :min="0"
                show-buttons
                button-layout="horizontal"
                decrement-button-icon="pi pi-minus"
                increment-button-icon="pi pi-plus"
                :readonly="getFieldReadonly"
                :disabled="getFieldDisabled"
                :style="{
                    '--p-inputgroup-addon-border-radius': '0',
                }"
            />
        </BaseInputWrapper>
    </fieldset>
</template>
